import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { driverAPI } from '../../services/api';
import CreateDriverModal from '../../components/company/CreateDriverModal';
import EditDriverModal from '../../components/company/EditDriverModal';
import DriverDetailModal from '../../components/company/DriverDetailModal';
import CompanyLayout from '../../components/layout/CompanyLayout';  // ← НЭМЭХ

const DriversManagement = () => {
  const { hasPermission } = useAuth();
  
  // ============================================
  // STATE
  // ============================================
  const [drivers, setDrivers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    is_active: '',
    page: 1,
    limit: 12
  });
  
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 1
  });

  // ============================================
  // FETCH DRIVERS
  // ============================================
  const fetchDrivers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        ...filters,
        is_active: filters.is_active === '' ? undefined : filters.is_active
      };
      
      const response = await driverAPI.getAll(params);

        console.log('📊 Full API Response:', response.data);
        console.log('📊 Stats object:', response.data.stats);
        console.log('📊 total_deliveries value:', response.data.stats?.total_deliveries);

        setDrivers(response.data.data);
        setStats(response.data.stats || null);  // ← null check
        setPagination(response.data.pagination);
      
    } catch (err) {
      console.error('❌ Fetch drivers error:', err);
      setError(err.response?.data?.message || 'Жолоочдын мэдээлэл татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [filters]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleSearch = (e) => {
    setFilters({ ...filters, search: e.target.value, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const handleCreate = () => {
    setCreateModalOpen(true);
  };

  const handleEdit = (driver) => {
    setSelectedDriver(driver);
    setEditModalOpen(true);
  };

  const handleViewDetail = (driver) => {
    setSelectedDriver(driver);
    setDetailModalOpen(true);
  };

  const handleDelete = async (driver) => {
    if (!confirm(`"${driver.driver_name}" жолоочийг идэвхгүй болгох уу?`)) {
      return;
    }

    try {
      await driverAPI.delete(driver.id);
      fetchDrivers();
      
      // Success notification (you can use toast library)
      alert('Жолооч амжилттай идэвхгүй болгогдлоо');
    } catch (err) {
      console.error('❌ Delete error:', err);
      alert(err.response?.data?.message || 'Жолооч устгахад алдаа гарлаа');
    }
  };

  const handleStatusToggle = async (driver) => {
    const newStatus = driver.status === 'available' ? 'offline' : 'available';
    
    try {
      await driverAPI.updateStatus(driver.id, newStatus);
      fetchDrivers();
    } catch (err) {
      console.error('❌ Status toggle error:', err);
      alert(err.response?.data?.message || 'Статус өөрчлөхөд алдаа гарлаа');
    }
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  const getStatusBadge = (status) => {
    const badges = {
      available: {
        bg: 'bg-green-100',
        text: 'text-green-700',
        label: '✓ Бэлэн',
        icon: '🟢'
      },
      busy: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        label: '⏳ Завгүй',
        icon: '🟡'
      },
      offline: {
        bg: 'bg-gray-100',
        text: 'text-gray-700',
        label: '⚫ Offline',
        icon: '⚫'
      }
    };
    
    const badge = badges[status] || badges.offline;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        <span className="text-base">{badge.icon}</span>
        {badge.label}
      </span>
    );
  };

  const getActiveBadge = (isActive) => {
    return isActive ? (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
        <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
        Идэвхтэй
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
        Идэвхгүй
      </span>
    );
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <span key={i} className="text-yellow-400 text-sm">
            {i < fullStars ? '★' : (i === fullStars && hasHalfStar ? '⯨' : '☆')}
          </span>
        ))}
        <span className="ml-1 text-xs text-gray-600 font-medium">
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  // ============================================
  // RENDER
  // ============================================
 return (
    <CompanyLayout>
      <div className="space-y-6">
      {/* ============================================ */}
      {/* HEADER */}
      {/* ============================================ */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            👨‍✈️ Жолоочийн удирдлага
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Жолоочдыг бүртгэх, засах, статистик харах
          </p>
        </div>
        
        {hasPermission('driver.create') && (
        <button
          onClick={handleCreate}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          <span className="text-xl">➕</span>
          Жолооч нэмэх
        </button>
        )}
      </div>

      {/* ============================================ */}
      {/* STATS CARDS */}
      {/* ============================================ */}
      {stats ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {/* Total */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
                <span className="text-3xl">👥</span>
                <span className="text-xs font-semibold text-blue-600 bg-blue-200 px-2 py-1 rounded-full">
                Нийт
                </span>
            </div>
            <div className="text-3xl font-bold text-blue-900 mb-1">
                {stats?.total ?? 0}
            </div>
            <div className="text-xs text-blue-700 font-medium">
                Жолоочийн тоо
            </div>
            </div>

            {/* Active */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border border-green-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
                <span className="text-3xl">✅</span>
                <span className="text-xs font-semibold text-green-600 bg-green-200 px-2 py-1 rounded-full">
                Идэвхтэй
                </span>
            </div>
            <div className="text-3xl font-bold text-green-900 mb-1">
                {stats?.active ?? 0}
            </div>
            <div className="text-xs text-green-700 font-medium">
                Ажиллаж байгаа
            </div>
            </div>

            {/* Available */}
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-5 border border-emerald-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
                <span className="text-3xl">🟢</span>
                <span className="text-xs font-semibold text-emerald-600 bg-emerald-200 px-2 py-1 rounded-full">
                Бэлэн
                </span>
            </div>
            <div className="text-3xl font-bold text-emerald-900 mb-1">
                {stats?.available ?? 0}
            </div>
            <div className="text-xs text-emerald-700 font-medium">
                Хүлээж байгаа
            </div>
            </div>

            {/* Busy */}
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-5 border border-yellow-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
                <span className="text-3xl">🟡</span>
                <span className="text-xs font-semibold text-yellow-600 bg-yellow-200 px-2 py-1 rounded-full">
                Завгүй
                </span>
            </div>
            <div className="text-3xl font-bold text-yellow-900 mb-1">
                {stats?.busy ?? 0}
            </div>
            <div className="text-xs text-yellow-700 font-medium">
                Хүргэлттэй
            </div>
            </div>

           {/* Deliveries */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 border border-purple-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
                <span className="text-3xl">📦</span>
                <span className="text-xs font-semibold text-purple-600 bg-purple-200 px-2 py-1 rounded-full">
                Нийт
                </span>
            </div>
            <div className="text-3xl font-bold text-purple-900 mb-1">
                {stats?.total_deliveries ?? 0}
            </div>
            <div className="text-xs text-purple-700 font-medium">
                Хүргэлт хийсэн
            </div>
            </div>
        </div>
        ) : (
        <div className="bg-gray-50 rounded-xl p-8 text-center text-gray-500">
            📊 Статистик уншиж байна...
        </div>
        )}

      {/* ============================================ */}
      {/* FILTERS */}
      {/* ============================================ */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🔍 Хайх
            </label>
            <input
              type="text"
              placeholder="Нэр, утас, и-мэйлээр хайх..."
              value={filters.search}
              onChange={handleSearch}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📊 Статус
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition"
            >
              <option value="">Бүгд</option>
              <option value="available">🟢 Бэлэн</option>
              <option value="busy">🟡 Завгүй</option>
              <option value="offline">⚫ Offline</option>
            </select>
          </div>

          {/* Active Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ✓ Идэвхтэй эсэх
            </label>
            <select
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition"
            >
              <option value="">Бүгд</option>
              <option value="true">Идэвхтэй</option>
              <option value="false">Идэвхгүй</option>
            </select>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* ERROR STATE */}
      {/* ============================================ */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <span className="text-2xl flex-shrink-0">⚠️</span>
            <div>
              <p className="font-semibold text-red-800">Алдаа гарлаа</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* LOADING STATE */}
      {/* ============================================ */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded"></div>
                <div className="h-3 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ============================================ */}
      {/* DRIVERS GRID */}
      {/* ============================================ */}
      {!loading && drivers.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {drivers.map((driver) => (
            <div
              key={driver.id}
              className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group hover:-translate-y-1"
            >
              {/* Card Header */}
              <div className="bg-gradient-to-r from-primary-50 to-primary-100 p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-200 rounded-full -mr-16 -mt-16 opacity-50"></div>
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                      {driver.driver_name?.charAt(0) || '👤'}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {getActiveBadge(driver.is_active)}
                      {getStatusBadge(driver.status)}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {driver.driver_name}
                  </h3>
                  <p className="text-sm text-gray-600 font-medium">
                    📞 {driver.phone}
                  </p>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-5 space-y-4">
                {/* Rating */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600 font-medium">Үнэлгээ:</span>
                  {renderStars(parseFloat(driver.rating) || 0)}
                </div>

                {/* Vehicle */}
                {driver.vehicle_number && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-lg">🚗</span>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{driver.vehicle_number}</div>
                      {driver.vehicle_type && (
                        <div className="text-xs text-gray-600">{driver.vehicle_type}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Stats */}
                {/* Stats - DEBUG VERSION */}
                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary-600">
                            {driver.total_deliveries || 0}
                            </div>
                            <div className="text-xs text-gray-600 font-medium">Нийт</div>
                            {/* DEBUG - ХАСАХ */}
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">
                            {driver.successful_deliveries || 0}
                            </div>
                            <div className="text-xs text-gray-600 font-medium">Амжилттай</div>
                        </div>
                        </div>

                {/* Actions */}
                <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => handleViewDetail(driver)}
                    className="px-3 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1"
                    title="Дэлгэрэнгүй"
                  >
                    <span className="text-sm">👁️</span>
                    <span className="hidden sm:inline">Харах</span>
                  </button>
                  
                  {hasPermission('driver.edit') && (
                  <button
                    onClick={() => handleEdit(driver)}
                    className="px-3 py-2 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1"
                    title="Засах"
                  >
                    <span className="text-sm">✏️</span>
                    <span className="hidden sm:inline">Засах</span>
                  </button>
                  )}
                  
                  {hasPermission('driver.delete') && (
                  <button
                    onClick={() => handleDelete(driver)}
                    className="px-3 py-2 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1"
                    title="Устгах"
                    disabled={!driver.is_active}
                  >
                    <span className="text-sm">🗑️</span>
                    <span className="hidden sm:inline">Устгах</span>
                  </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ============================================ */}
      {/* EMPTY STATE */}
      {/* ============================================ */}
      {!loading && drivers.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="text-8xl mb-6">🚗</div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Жолооч байхгүй байна
          </h3>
          <p className="text-gray-600 mb-6">
          {filters.search || filters.status || filters.is_active
            ? 'Хайлтын үр дүн олдсонгүй. Өөр утга оруулж үзнэ үү.'
            : 'Эхний жолоочоо нэмээд эхэлцгээе!'}
        </p>
        {hasPermission('driver.create') && (
        <button
          onClick={handleCreate}
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
        >
          <span className="text-xl">➕</span>
          Жолооч нэмэх
        </button>
        )}
        </div>
      )}

      {/* ============================================ */}
      {/* PAGINATION */}
      {/* ============================================ */}
      {!loading && drivers.length > 0 && pagination.pages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-600">
            Нийт <span className="font-semibold text-gray-900">{pagination.total}</span> жолооч
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
            >
              ← Өмнөх
            </button>
            
            <span className="px-4 py-2 text-sm font-medium text-gray-700">
              {pagination.page} / {pagination.pages}
            </span>
            
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
            >
              Дараах →
            </button>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODALS */}
      {/* ============================================ */}
      {createModalOpen && (
        <CreateDriverModal
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={() => {
            setCreateModalOpen(false);
            fetchDrivers();
          }}
        />
      )}

      {editModalOpen && selectedDriver && (
        <EditDriverModal
          isOpen={editModalOpen}
          driver={selectedDriver}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedDriver(null);
          }}
          onSuccess={() => {
            setEditModalOpen(false);
            setSelectedDriver(null);
            fetchDrivers();
          }}
        />
      )}

      {detailModalOpen && selectedDriver && (
        <DriverDetailModal
          isOpen={detailModalOpen}
          driver={selectedDriver}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedDriver(null);
          }}
          onEdit={() => {
            setDetailModalOpen(false);
            handleEdit(selectedDriver);
          }}
        />
            )}
      </div>
    </CompanyLayout>
  );
};

export default DriversManagement;